import { ReportComponentType } from '@/types';

export type TableOfContentsItemType = 'heading' | 'report-component';

export interface TableOfContentsItem {
    element: HTMLElement;
    id: string;
    key: string;
    level: number;
    reportComponentId?: string;
    reportComponentType?: ReportComponentType;
    title: string;
    type: TableOfContentsItemType;
}

export interface TableOfContentsScanOptions {
    excludeHidden?: boolean;
}

const tableOfContentsElementIdCache = new WeakMap<HTMLElement, string>();

const slugify = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .slice(0, 48);

const getHeadingLevel = (element: Element) => Number(element.tagName.replace('H', ''));

const getTableOfContentsId = (element: HTMLElement, fallback: string) => {
    const existingId =
        element.getAttribute('data-table-of-contents-id') ||
        element.getAttribute('data-memo-toc-id') ||
        element.id ||
        tableOfContentsElementIdCache.get(element);
    const id = existingId || fallback;

    tableOfContentsElementIdCache.set(element, id);

    return id;
};

const isHidden = (element: HTMLElement) => {
    if (
        element.hidden ||
        element.getAttribute('aria-hidden') === 'true' ||
        element.getAttribute('data-table-of-contents-hidden') === 'true'
    ) {
        return true;
    }

    const style = window.getComputedStyle(element);

    return style.display === 'none' || style.visibility === 'hidden';
};

const isVisibleWithinRoot = (element: HTMLElement, root: HTMLElement) => {
    let current: HTMLElement | null = element;

    while (current) {
        if (isHidden(current)) {
            return false;
        }

        if (current === root) {
            break;
        }

        current = current.parentElement;
    }

    return true;
};

const collectAccessibleLabelText = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? '';
    }

    if (!(node instanceof HTMLElement) && !(node instanceof SVGElement)) {
        return '';
    }

    if (
        node.getAttribute('data-table-of-contents-label-ignore') === 'true' ||
        node.getAttribute('aria-hidden') === 'true' ||
        (node instanceof HTMLElement && isHidden(node))
    ) {
        return '';
    }

    return Array.from(node.childNodes).map(collectAccessibleLabelText).join(' ');
};

const getHeadingTitle = (element: HTMLElement) => {
    const explicitTitle =
        element.getAttribute('data-table-of-contents-title') || element.getAttribute('data-memo-toc-title');

    if (explicitTitle) {
        return explicitTitle.trim();
    }

    return Array.from(element.childNodes).map(collectAccessibleLabelText).join(' ').replace(/\s+/g, ' ').trim();
};

export const scanTableOfContents = (
    root?: HTMLElement | null,
    options: TableOfContentsScanOptions = {},
): TableOfContentsItem[] => {
    if (!root) {
        return [];
    }

    const candidates = Array.from(
        root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, [data-memo-report-component-id]'),
    );
    const seenIds = new Set<string>();
    const seenReportComponentIds = new Set<string>();

    return candidates.reduce<TableOfContentsItem[]>((items, element, index) => {
        if (options.excludeHidden && !isVisibleWithinRoot(element, root)) {
            return items;
        }

        const isHeading = /^H[1-6]$/.test(element.tagName);
        const reportComponentId = element.getAttribute('data-memo-report-component-id') ?? undefined;
        const containingReportComponent = element.closest<HTMLElement>('[data-memo-report-component-id]');
        const containingIgnoredAtom = element.closest<HTMLElement>(
            '[data-memo-toc-ignore="true"], [data-table-of-contents-ignore="true"], [data-type="unfurled-link"], unfurled-link',
        );

        if (containingReportComponent && containingReportComponent !== element) {
            return items;
        }

        if (isHeading) {
            if (containingIgnoredAtom) {
                return items;
            }

            const title = getHeadingTitle(element);

            if (!title) {
                return items;
            }

            const level = getHeadingLevel(element);
            const id = getTableOfContentsId(element, `memo-heading-${index}-${slugify(title) || 'heading'}`);

            if (seenIds.has(id)) {
                return items;
            }

            seenIds.add(id);
            items.push({
                element,
                id,
                key: id,
                level,
                title,
                type: 'heading',
            });

            return items;
        }

        if (reportComponentId) {
            if (seenReportComponentIds.has(reportComponentId)) {
                return items;
            }

            const title =
                element.getAttribute('data-table-of-contents-title') ||
                element.getAttribute('data-memo-toc-title') ||
                element.querySelector('[data-memo-toc-title]')?.textContent?.trim() ||
                'Report Component';
            const reportComponentType = element.getAttribute('data-memo-report-component-type') as
                | ReportComponentType
                | undefined;
            const id = getTableOfContentsId(element, `memo-report-component-${reportComponentId}`);

            if (seenIds.has(id)) {
                return items;
            }

            seenIds.add(id);
            seenReportComponentIds.add(reportComponentId);
            items.push({
                element,
                id,
                key: id,
                level: 2,
                reportComponentId,
                reportComponentType,
                title,
                type: 'report-component',
            });
        }

        return items;
    }, []);
};
